import { type AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useTheme } from "@teispace/next-themes";

type ThemeContext = ReturnType<typeof useTheme>;

// Schema imports
import { sideContactListSchema } from "./sideNavigation/contactList.schema";
import { sideThreadListSchema } from "./sideNavigation/threadList.schema";
import { importCSVSidebarSchema } from "./sideNavigation/crm/importCSV.sidebar";
import {
  SideNavigationComponent,
  SideNavigationV2Props,
  type ListComponent,
  type SidebarComponent,
  type PageV2Component,
  type ThreadsSidebarComponent,
  type CustomComponent,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { RootState, store } from "@store/index";
import { sideNavigationListSchema } from "./sideNavigation/navigationList.schema";
import { editModelViewSchema } from "./sideNavigation/crm/editModel.schema";
import { editRelationshipViewSchema } from "./sideNavigation/crm/editRelationship.schema";
import { editFieldViewSchema } from "./sideNavigation/crm/editField2.schema";
import { editTriggerViewSchema } from "./sideNavigation/workflows/editTrigger.schema";
import { runWorkflowSchema } from "./sideNavigation/workflows/run.schema";
// buildVersionSchema removed - use DEPLOYMENT_LOGS instead
import { addChannelViewSchema } from "./sideNavigation/chat/addChannel.schema";
import { deploymentLogsSchema } from "./sideNavigation/deployments/deploymentLogs.schema";
import { provisionLogsSchema } from "./sideNavigation/provisions/provisionLogs.schema";
import { migrationHistorySchema } from "./sideNavigation/crm/migrationHistory.schema";
import { schemaMigrationPreviewSchema } from "./sideNavigation/crm/schemaMigrationPreview.schema";
import { teamWorkspaceImportPreviewSchema } from "./sideNavigation/teamWorkspaceImportPreview.schema";
import { generatedReportSchema } from "./sideNavigation/reports/generatedReport.schema";
import { recentWorkflowRunsSchema } from "./sideNavigation/workflows/recentRuns.schema";
import { editAgentFlowSchema } from "./sideNavigation/agents/editAgentFlow.schema";
import { addToolBindingFlowSchema } from "./sideNavigation/agents/addToolBindingFlow.schema";
import { addAgentToolConnectionSchema } from "./sideNavigation/agents/addAgentToolConnection.schema";
import {
  editStageSchema,
  type EditStageParams,
} from "./sideNavigation/agents/editStage.schema";
import { entitySetupSidebarSchema } from "./sideNavigation/apps/entitySetup.schema";

import {
  open as openSideNavigation,
  close as closeSideNavigation,
} from "@store/features/layout/sideNavigationSlice";

// Types
export type SideNavigationMode = "desktop" | "mobile";
export type SideNavigationPosition = "left" | "right";

export type SideNavigationSchemaProps = {
  mode: SideNavigationMode;
  id: string;
  position: SideNavigationPosition;
  width: string;
  mobileWidth?: string;
  handleClose?: () => void | Promise<void>;
  themeProvider?: ThemeContext;
  router: AppRouterInstance;
};

export type SchemaName = keyof typeof SCHEMA_MAP;

// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const getSideNavigationParams = (
  state?: Record<string, string | string[] | undefined | null>,
): Record<string, string | string[] | undefined | null> => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {
    ...state,
  };
};

export type SideNavigationSchemaType =
  | "CONTACT_LIST"
  | "THREAD_LIST"
  | "NAVIGATION_LIST"
  | "EDIT_MODEL"
  | "EDIT_RELATIONSHIP"
  | "EDIT_FIELD"
  | "EDIT_TRIGGER"
  | "WORKFLOW_RUN"
  | "RECENT_WORKFLOW_RUNS"
  | "ADD_CHANNEL"
  | "IMPORT_CSV"
  | "DEPLOYMENT_LOGS"
  | "PROVISION_LOGS"
  | "MIGRATION_HISTORY"
  | "SCHEMA_MIGRATION_PREVIEW"
  | "TEAM_WORKSPACE_IMPORT_PREVIEW"
  | "GENERATED_REPORT"
  | "EDIT_AGENT_FLOW"
  | "ADD_TOOL_BINDING"
  | "ADD_AGENT_TOOL_CONNECTION"
  | "EDIT_STAGE"
  | "ENTITY_SETUP";

// Custom error for schema generation
export class SchemaGenerationError extends Error {
  constructor(schema: string, message: string) {
    super(`Error generating schema '${schema}': ${message}`);
    this.name = "SchemaGenerationError";
  }
}

// Schema registry with type checking and adapter functions
export const SCHEMA_MAP = {
  CONTACT_LIST: sideContactListSchema,
  THREAD_LIST: sideThreadListSchema,
  NAVIGATION_LIST: sideNavigationListSchema,
  EDIT_MODEL: editModelViewSchema,
  EDIT_RELATIONSHIP: editRelationshipViewSchema,
  EDIT_FIELD: editFieldViewSchema,
  EDIT_TRIGGER: editTriggerViewSchema,
  WORKFLOW_RUN: runWorkflowSchema,
  RECENT_WORKFLOW_RUNS: recentWorkflowRunsSchema,
  ADD_CHANNEL: addChannelViewSchema,
  IMPORT_CSV: importCSVSidebarSchema,
  DEPLOYMENT_LOGS: deploymentLogsSchema,
  PROVISION_LOGS: provisionLogsSchema,
  MIGRATION_HISTORY: migrationHistorySchema,
  SCHEMA_MIGRATION_PREVIEW: schemaMigrationPreviewSchema,
  TEAM_WORKSPACE_IMPORT_PREVIEW: teamWorkspaceImportPreviewSchema,
  GENERATED_REPORT: generatedReportSchema,
  EDIT_AGENT_FLOW: editAgentFlowSchema,
  ADD_TOOL_BINDING: addToolBindingFlowSchema,
  ADD_AGENT_TOOL_CONNECTION: addAgentToolConnectionSchema,
  EDIT_STAGE: editStageSchema,
  ENTITY_SETUP: entitySetupSidebarSchema,
} as const;

export type SideNavigationComponentProps<
  T extends Record<string, unknown>,
  P extends Record<string, unknown> | null,
> = {
  props: T;
  position: SideNavigationPosition;
  width: string;
  viewPortIsMobile: boolean;
  themeContext: ThemeContext;
  routerContext: {
    router: AppRouterInstance;
    params: P;
    pathname: string;
  };
  state: RootState;
};

export function generateSideNavigationComponent<
  T extends Record<string, unknown>,
  P extends Record<string, string | string[] | undefined> | null,
>(
  args: SideNavigationComponentProps<T, P>,
): SideNavigationV2Props<SideNavigationComponent> {
  const sideNavState = args.state.sideNavigation[args.position];
  const schema = sideNavState.schema;

  // Create base props that are common to all schemas
  const baseProps = {
    position: args.position,
    width: args.width,
    viewPortIsMobile: args.viewPortIsMobile,
    themeContext: args.themeContext,
    routerContext: {
      router: args.routerContext.router,
      params: { subdomain: args.routerContext.params?.subdomain as string },
      pathname: args.routerContext.pathname,
    },
    state: args.state,
  };

  let generatedProps: SideNavigationV2Props<SideNavigationComponent>;

  // Type-safe schema selection
  switch (schema) {
    case "CONTACT_LIST": {
      const schemaProps: SideNavigationComponentProps<
        ListComponent,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as ListComponent,
      };
      generatedProps = sideContactListSchema(schemaProps);
      break;
    }
    case "THREAD_LIST": {
      const schemaProps: SideNavigationComponentProps<
        ThreadsSidebarComponent,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as ThreadsSidebarComponent,
      };
      generatedProps = sideThreadListSchema(schemaProps);
      break;
    }
    case "NAVIGATION_LIST": {
      const schemaProps: SideNavigationComponentProps<
        SidebarComponent,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as SidebarComponent,
      };
      generatedProps = sideNavigationListSchema(schemaProps);
      break;
    }
    case "EDIT_MODEL": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = editModelViewSchema(schemaProps);
      break;
    }
    case "EDIT_RELATIONSHIP": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = editRelationshipViewSchema(schemaProps);
      break;
    }
    case "EDIT_FIELD": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = editFieldViewSchema(schemaProps);
      break;
    }
    case "EDIT_TRIGGER": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = editTriggerViewSchema(schemaProps);
      break;
    }
    case "WORKFLOW_RUN": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = runWorkflowSchema(schemaProps);
      break;
    }
    case "RECENT_WORKFLOW_RUNS": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = recentWorkflowRunsSchema(schemaProps);
      break;
    }
    case "ADD_CHANNEL": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = addChannelViewSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: {
            subdomain: string;
            phoneNumbers: { id: string; phone: string }[];
            name: string;
            identifierValue: string;
          };
        },
      );
      break;
    }
    case "IMPORT_CSV": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = importCSVSidebarSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: {
            subdomain: string;
            fileId: string;
            modelId: string;
            modelName: string;
            modelNamePlural: string;
            modelHandlePlural?: string;
            totalRecords: number;
            uniqueFields: Array<{ id: string; label: string; handle: string }>;
            importId: string;
            importMode?: string;
            matchOnFieldId?: string;
            associationCandidates?: Array<{
              relationField: { id: string; label: string; handle: string };
              parentModel: { id: string; label: string; handle: string };
              parentUniqueFields: Array<{
                id: string;
                label: string;
                handle: string;
                csvPathSuffix?: string;
              }>;
            }>;
            associationRules?: Array<{
              relationFieldId: string;
              parentModelId: string;
              parentFieldId: string;
              csvPath?: string | null;
              updateExistingBehavior: "update" | "preserve";
              autoCreateIfNotFound: boolean;
              rejectIfNotFound: boolean;
            }>;
          };
        },
      );
      break;
    }
    case "DEPLOYMENT_LOGS": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = deploymentLogsSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: {
            deploymentId: string;
          };
        },
      );
      break;
    }
    case "PROVISION_LOGS": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = provisionLogsSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: {
            provisionId: string;
          };
        },
      );
      break;
    }
    case "MIGRATION_HISTORY": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = migrationHistorySchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: Record<string, unknown>;
        },
      );
      break;
    }
    case "SCHEMA_MIGRATION_PREVIEW": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = schemaMigrationPreviewSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: Record<string, unknown>;
        },
      );
      break;
    }
    case "TEAM_WORKSPACE_IMPORT_PREVIEW": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = teamWorkspaceImportPreviewSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: Record<string, unknown>;
        },
      );
      break;
    }
    case "GENERATED_REPORT": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = generatedReportSchema(
        schemaProps as SideNavigationComponentProps<
          PageV2Component,
          { subdomain: string }
        > & {
          props: {
            reportHandle: string;
            reportName: string;
            html: string;
            reportUrl: string;
          };
        },
      );
      break;
    }
    case "EDIT_AGENT_FLOW": {
      const sideNavParams = sideNavState.params as {
        llmModels?: Array<{
          value: string;
          label: string;
          description?: string;
        }>;
      };
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        {
          subdomain: string;
          llmModels?: Array<{
            value: string;
            label: string;
            description?: string;
          }>;
        }
      > = {
        ...baseProps,
        routerContext: {
          ...baseProps.routerContext,
          params: {
            subdomain: args.routerContext.params?.subdomain as string,
            llmModels: sideNavParams.llmModels,
          },
        },
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = editAgentFlowSchema(schemaProps);
      break;
    }
    case "ADD_TOOL_BINDING": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = addToolBindingFlowSchema(schemaProps);
      break;
    }
    case "ADD_AGENT_TOOL_CONNECTION": {
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = addAgentToolConnectionSchema(schemaProps);
      break;
    }
    case "EDIT_STAGE": {
      const sideNavParams = sideNavState.params as EditStageParams;
      const schemaProps: SideNavigationComponentProps<
        PageV2Component,
        EditStageParams
      > = {
        ...baseProps,
        routerContext: {
          ...baseProps.routerContext,
          params: {
            ...sideNavParams,
          },
        },
        props: args.props as unknown as PageV2Component,
      };
      generatedProps = editStageSchema(schemaProps);
      break;
    }
    case "ENTITY_SETUP": {
      const schemaProps: SideNavigationComponentProps<
        CustomComponent,
        { subdomain: string }
      > = {
        ...baseProps,
        props: args.props as unknown as CustomComponent,
      };
      generatedProps = entitySetupSidebarSchema(schemaProps);
      break;
    }
    default: {
      throw new SchemaGenerationError(schema, "Unknown schema type");
    }
  }
  /**
   * override the onSidebarOpenChange to dispatch the open or close side navigation action
   */
  return {
    ...generatedProps,
    onSidebarOpenChange: (open: boolean) => {
      // run the on close action
      void generatedProps.onSidebarOpenChange?.(open);
      if (open) {
        void store.dispatch(
          openSideNavigation({
            position: args.position,
            schema: sideNavState.schema,
            props: {
              width: args.width,
              mobileWidth: args.width, // Using width as mobileWidth since it's not in the props anymore
            },
          }),
        );
      } else {
        void store.dispatch(closeSideNavigation({ position: args.position }));
      }
    },
  };
}
